<template>
  <div class="page-editor-toolbar">
    <b-button-group>
      <b-button :disabled='isDisabled' variant="info" size="lg" @click="savePage">Save</b-button>
      <b-button v-if="authorize(permissibleActions.clipboardAble.minPermissionLevel)" :disabled='isCopyDisabled' variant="info" size="lg" @click="copyAction(focus)"><font-awesome-icon icon="copy" /></b-button>
      <b-button v-if="authorize(permissibleActions.clipboardAble.minPermissionLevel)" :disabled='canYouPaste' variant="info" size="lg" @click="pasteAction"><font-awesome-icon icon="paste" /></b-button>
      <b-button v-if="authorize(permissibleActions.clipboardAble.minPermissionLevel)" :disabled='isUndoDisabled' variant="info" size="lg" @click="undoAction"><font-awesome-icon icon="undo" /></b-button>
      <b-button v-if="authorize(permissibleActions.clipboardAble.minPermissionLevel)" :disabled='true' variant="info" size="lg" @click="redoAction"><font-awesome-icon icon="redo" /></b-button>
      <b-button v-if="authorize(permissibleActions.nestable.minPermissionLevel)" :disabled='isDisabled' variant="primary" size="lg" @click="showNewModal"><font-awesome-icon icon="folder-plus" /></b-button>
      <b-button :disabled='isDisabled' variant="success" size="lg" @click="selectPage"><font-awesome-icon icon="cogs" /></b-button>
      <b-button v-if="authorize(permissibleActions.publishable.minPermissionLevel)" :disabled='isDisabled' variant="info" size="lg" @click="showPublishModal">Publish</b-button>
      <router-link :disabled='isDisabled' :to="{ name: 'PagePreview' }" tag="a" target="_blank" class="btn btn-lg btn-primary">Preview</router-link>
      <b-button variant="info" size="lg" @click="showHelpModal">Help</b-button>
    </b-button-group>

    <!-- Modal Component -->
    <b-modal ref="publishModal" title="Confirm Kiosk Publish" ok-variant="primary" ok-title="Publish" @ok="publishBundle" :ok-disabled="bundle.name === undefined || bundle.name === null || bundle.name === ''">
      <p>
        Are you sure? Publishing will create a new <strong>bundle.</strong> Please click on <strong>Update</strong> to send an update to existing bundles.
      </p>
      <div class="mt-1 mb-1">
        <b-button variant="primary" size="md" @click="publishExistingBundles">Update All</b-button>
      </div>
      <b-form>
        <b-form-group id="bundle-name" label="Name" label-for="bundle-name" description="Bundle Name">
          <b-form-input type="text" placeholder="Bundle Name" required v-model="bundle.name" />
        </b-form-group>

        <b-form-group id="bundle-street-address" label="Street Address" label-for="bundle-street-address" description="Bundle Name">
          <b-form-input type="text" placeholder="Street Address" required v-model="bundle.street_address_1" />
        </b-form-group>

        <b-form-group id="bundle-secondary-street-address" label="Street Address 2" label-for="bundle-secondary-street-address" description="Bundle Name">
          <b-form-input type="text" placeholder="Street Address 2" required v-model="bundle.street_address_2" />
        </b-form-group>

        <b-form-group id="bundle-city" label="City" label-for="bundle-city" description="Bundle City">
          <b-form-input type="text" placeholder="City" required v-model="bundle.city" />
        </b-form-group>

        <b-form-group id="bundle-state" label="State" label-for="bundle-state" description="Bundle State">
          <b-form-input type="text" placeholder="State" required v-model="bundle.state" />
        </b-form-group>

        <b-form-group id="bundle-country" label="Country" label-for="bundle-country" description="Bundle Country">
          <b-form-input type="text" placeholder="Country" required v-model="bundle.country" />
        </b-form-group>

        <b-form-group id="bundle-zipcode" label="Zip Code" label-for="bundle-zipcode" description="Bundle zipcode">
          <b-form-input type="text" placeholder="zip code" required v-model="bundle.zipcode" />
        </b-form-group>
      </b-form>
    </b-modal>

    <b-modal ref="publishModalProgress" title="Kiosk Publish Progress" :hide-footer="true" @close="checkPublishInterval">
      <p v-if="currentJid">{{ publishModalProgress[currentJid].message }}</p>
      <p>Time Elapsed: {{ getTime }}</p>
      <b-progress :max="5">
        <b-progress-bar v-if="currentJid" :value="publishModalProgress[currentJid].progress" show-progress animated>
          {{ (publishModalProgress[currentJid].progress / 5).toFixed(2) * 100 }}%
        </b-progress-bar>
      </b-progress>
      <a v-if="currentJid && publishModalProgress[currentJid].downloadLink" :href="publishModalProgress[currentJid].downloadLink" class="mt-1">Click Here to Download</a>
    </b-modal>

    <b-modal ref="helpModal" title="Help" :hide-footer="true">
      <h1>Help</h1>
      <hr />
      <h3>General Notes</h3>
      <div class="help-content">
        <p>When you are done manipulating an object make sure to lock it so you don't accidentally move it or resize it. Use <b>&lt;cmd&gt; + &lt;L&gt;</b> to lock it while it's in focus.</p>
      </div>
      <h3>Keyboard Shortcuts</h3>
      <div class="help-content">
        <ul>
          <li>
            <b>&lt;cmd&gt; + &lt;C&gt;</b> - Copy currently selected object
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;V&gt;</b> - Paste any copied object
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;Z&gt;</b> - Undo last action. *NOTE* this can be irreversible
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;L&gt;</b> - Sets currently selected object to lock or unlock
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;H&gt;</b> - Hides currently selected object
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;D&gt;</b> - Clones currently selected object
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;A&gt;</b> - Expands currently selected object to fit parent
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;1&gt;</b> - Horizontally and vertically centers currently selected object to parent
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;2&gt;</b> - Horizontally centers currently selected object to parent
          </li>
          <li>
            <b>&lt;cmd&gt; + &lt;3&gt;</b> - Vertically centers currently selected object to parent
          </li>
          <li>
            <b>&lt;delete&gt;</b> - Delete currently selected object
          </li>
          <li>
            <b>&lt;esc&gt;</b> - Deselect currently selected object
          </li>
          <li>
            <b>hold &lt;shift&gt;</b> - When resizing object, resize from the middle instead of relative corner
          </li>
          <li>
            <b>&lt;up arrow&gt;</b> - Move currently selected object up 4px
          </li>
          <li>
            <b>&lt;down arrow&gt;</b> - Move currently selected object down 4px
          </li>
          <li>
            <b>&lt;left arrow&gt;</b> - Move currently selected object left 4px
          </li>
          <li>
            <b>&lt;right arrow&gt;</b> - Move currently selected object right 4px
          </li>
        </ul>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import EditorEvents from '../mixins/editor-events';
import { BundleService } from '@/common/services/bundle.service';
import ActionCableService from '@/common/services/action_cable.service';
import PageEditorPermissionSubsystem from '../mixins/page-editor-permissions-subsystem';
import { usePageEditorStore } from '@/stores/PageEditorStore';

export default {
  name: 'PageEditorToolbar',
  mixins: [EditorEvents, PageEditorPermissionSubsystem],
  props: {
    focus: Object,
    userInteraction: Object,
    lockInteraction: Boolean,
    eventBuffer: Array,
    copyBuffer: Object,
  },
  setup() {
    const pageEditorStore = usePageEditorStore();
    return {
      pageEditorStore,
    };
  },
  data() {
    return {
      app: null,
      subscription: null,
      bundle: {},
      publishModalProgress: {},
      currentJid: null,
      elapsedTime: 0,
      startTimer: null,
      minPermissionLevel: null,
    };
  },
  created() {
    this.app = ActionCableService.createConnection();
    this.createActionCable(this.pageEditorStore.getPage.kiosk_id);
    this.permissionLevel = this.pageEditorStore.getPermissionLevel;
  },
  beforeDestroy() {
    if (this.startTimer) clearInterval(this.startTimer);

    if (this.subscription) {
      this.subscription.unsubscribe();
      if (this.app) {
        this.app.disconnect();
      }
    }
  },
  computed: {
    isDisabled() {
      return this.lockInteraction;
    },
    isUndoDisabled() {
      let result = this.lockInteraction;
      result = !(this.eventBuffer.length > 0 && result === false);
      return result;
    },
    getTime() {
      return moment()
        .hour(0)
        .minute(0)
        .second(this.elapsedTime)
        .format('HH:mm:ss');
    },
    isCopyDisabled() {
      let result = this.lockInteraction;
      if (result === false) {
        if (!this.focus) {
          result = true;
        } else if (this.focus.object_model_name === 'Page') {
          result = true;
        } else if (this.focus.object_model_name === 'Kiosk') {
          result = true;
        }
      }
      return result;
    },
    canYouPaste() {
      let result = this.lockInteraction;
      if (result === false) {
        if (!this.copyBuffer.id) {
          result = true;
        }
      }
      return result;
    },
  },
  methods: {
    checkPublishInterval() {
      if (this.startTimer) clearInterval(this.startTimer);
    },
    publishBundle() {
      // reset everything before reconnecting
      this.publishModalProgress = {};
      this.currentJid = null;

      // timer
      this.elapsedTime = 0;
      clearInterval(this.startTimer);

      // restart timer
      this.startTimer = setInterval(() => {
        this.elapsedTime += 1;
      }, 1000);

      BundleService.create(this.pageEditorStore.getPage.kiosk_id, this.bundle)
        .then(() => {
          this.$refs.publishModalProgress.show();
          this.bundle = {};
        });
    },
    publishExistingBundles() {
      BundleService.export(this.pageEditorStore.getPage.kiosk_id)
        .then(() => {
          this.bundle = {};
          this.$refs.publishModal.hide();
        });
    },
    showPublishModal() {
      this.$refs.publishModal.show();
    },
    showHelpModal() {
      this.$refs.helpModal.show();
    },
    toggleStageVisible(){
      this.pageEditorStore.toggleStageVisible()
    },
    createActionCable(kioskId) {
      const self = this;

      self.subscription = ActionCableService.createSubscription(this.app, 'KioskCompileChannel', kioskId, {
        connected() {
          self.log('createActionCable', 'connected');
        },
        received(data) {
          self.log('createActionCable', 'received', data);
          if (self.currentJid === null) self.currentJid = data.jid;

          if (!Object.prototype.hasOwnProperty.call(self.publishModalProgress, self.currentJid)) self.publishModalProgress[self.currentJid] = {};

          self.publishModalProgress[self.currentJid].message = data.message;
          self.publishModalProgress[self.currentJid].progress = data.progress;

          // this means that it's finally done.
          if (data.download_link) {
            self.publishModalProgress[self.currentJid].downloadLink = data.download_link;
            clearInterval(self.startTimer);
            self.$forceUpdate();
          }
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .page-editor-toolbar {
    height: 50px;
    background-color: #c4c4c4;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .help-content {
    text-align: left;
  }
</style>
